'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useRenderProgress } from '../hooks/use-render-progress';
import type { RemainingTime } from '../hooks/use-render-progress';
import { useLoadingContent } from '../hooks/use-loading-content';
import { useLoadingFacts } from '../hooks/use-loading-facts';
import { useDesignStage } from '../hooks/use-design-stage';
import { LoadingAnimation } from '@/shared/ui';
import { useFitText } from '@/shared/hooks';
import { Card, CardContent } from '@/shared/ui/shadcn/card';
import { Progress } from '@/shared/ui/shadcn/progress';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { Badge } from '@/shared/ui/shadcn/badge';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { cn } from '@/shared/lib/cn';
import type { Render } from '@/types/render';
import type { Control } from '@/types/concept';
import type { Job } from '@/features/design/job-queue';

export type RenderProgressMode = 'full' | 'inline' | 'bar' | 'minimal';

interface RenderProgressProps {
  render?: Render;
  control?: Control;
  queueRenderInfo?: Job; // Optional: full queue info for auto-detection
  mode?: RenderProgressMode;
  className?: string;
  projectId?: string;
  conceptId?: string;
  onComplete?: () => void;
  onError?: (error: string) => void;
  showContent?: boolean;
  showAnimation?: boolean;
  isUpscale?: boolean; // Flag to indicate this is an upscale operation (auto-detected from queueRenderInfo if available)
  /**
   * Render finished, image still prefetching (inline mode only). Shows the
   * bar pinned at 100% and suppresses the internal completion fade — the
   * parent (useSmoothRenderOverlay) owns the dissolve, timed to the image
   * being decoded. Mirrors Apple/Android `isPreloadingRender`.
   */
  isPreloading?: boolean;
}

type AnimationType = 'blueprint' | 'construction' | 'morph' | 'dots';

export function RenderProgress({
  render,
  control,
  queueRenderInfo,
  mode = 'full',
  className = '',
  projectId,
  conceptId,
  onComplete,
  onError,
  showContent = true,
  showAnimation = true,
  isUpscale = false,
  isPreloading = false,
}: RenderProgressProps) {
  const entityType = render ? 'render' : 'control';
  const entity = render || control;
  const tProgress = useTranslations('render.progress');
  const t = useTranslations('renderProgress');

  const formatRemaining = (time: RemainingTime | null): string | null => {
    if (!time) return null;
    return tProgress(
      time.type === 'minutes' ? 'minutesRemaining' : 'secondsRemaining',
      { count: time.count }
    );
  };

  // Auto-detect if this is an upscale operation from queueRenderInfo
  const isUpscaleOperation =
    queueRenderInfo?.jobType === 'upscale' || isUpscale;

  const {
    progress,
    error,
    isQueued,
    isProcessing,
    isCompleted,
    isFailed,
    isRetrying,
    isStalling,
    remainingTime,
  } = useRenderProgress({
    render,
    control,
    mode: entityType,
    onComplete,
    onError,
  });

  const { data: loadingFactsData } = useLoadingFacts(projectId);

  // Small rotating "design stage" narrated above the progress bar (Analyzing
  // materials, Verifying structural solutions, …) — the architect/engineer work
  // that sets us apart from tools that just generate a picture.
  const currentStage = useDesignStage(
    loadingFactsData?.stages,
    progress,
    isProcessing && !isUpscaleOperation
  );

  const { currentContent, userStats } = useLoadingContent({
    isActive: isProcessing,
    trackRender: entityType === 'render' && isProcessing,
    contextualFacts: loadingFactsData?.facts,
    seenIds: loadingFactsData?.seen_ids,
    // Absent flag (older API) → enabled.
    enabled: loadingFactsData?.loading_facts_enabled ?? true,
  });

  // The fact renders at the size the design asks for and only shrinks if it
  // actually overflows its box (long fact, short landscape card, verbose
  // locale). Mirrors Apple `minimumScaleFactor` / Android `TextAutoSize`.
  const factRef = useFitText<HTMLParagraphElement>(currentContent?.text);

  const [animationType, setAnimationType] =
    useState<AnimationType>('blueprint');
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [wasAlreadyCompleted] = useState(isCompleted);

  // Rotate animation type
  useEffect(() => {
    if (!isProcessing || mode === 'minimal' || mode === 'bar') return;

    // For upscale, use only 'morph' animation (image enhancement)
    if (isUpscaleOperation) {
      setAnimationType('morph');
      return;
    }

    const animations: AnimationType[] = ['blueprint', 'construction', 'morph'];
    let currentIndex = 0;

    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % animations.length;
      const nextAnimation = animations[currentIndex];
      if (nextAnimation) {
        setAnimationType(nextAnimation);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isProcessing, mode, isUpscaleOperation]);

  // Handle completion with fade out for inline mode. While the parent holds
  // the overlay for the image prefetch (isPreloading), the dissolve belongs
  // to it — the internal fade must not race a second, faster opacity drop.
  useEffect(() => {
    if (isCompleted && !isPreloading && mode === 'inline' && !isFadingOut) {
      setIsFadingOut(true);
      setTimeout(() => {
        onComplete?.();
      }, 500);
    }
  }, [isCompleted, isPreloading, mode, isFadingOut, onComplete]);

  // Auto-redirect for already completed renders
  useEffect(() => {
    if (wasAlreadyCompleted && isCompleted && mode === 'full') {
      const timer = setTimeout(() => {
        onComplete?.();
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [wasAlreadyCompleted, isCompleted, mode, onComplete]);

  // Render based on mode
  if (mode === 'bar') {
    if (!isProcessing && !isQueued) return null;

    return (
      <div className={cn('w-full', className)}>
        {isQueued ? (
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden flex items-center justify-center">
            <LoadingAnimation type="dots" size="sm" />
          </div>
        ) : (
          <Progress value={progress} className="h-2" isStalling={isStalling} />
        )}
      </div>
    );
  }

  if (mode === 'minimal') {
    if (!isProcessing && !isQueued) return null;

    return (
      <div className={cn('flex items-center gap-2', className)}>
        {isQueued ? (
          <>
            <LoadingAnimation type="dots" size="sm" />
            <span className="text-list-item-subtitle text-muted-foreground">{t('waiting')}</span>
          </>
        ) : (
          <>
            <Progress
              value={progress}
              className="h-1.5 w-24"
              isStalling={isStalling}
            />
            <span className="text-progress-value text-muted-foreground">{progress}%</span>
          </>
        )}
      </div>
    );
  }

  if (mode === 'inline') {
    const shouldShow =
      isQueued || isProcessing || isPreloading || (isCompleted && isFadingOut);
    // Hide progress on failure - toast will handle error display
    if (!shouldShow || isFailed) return null;

    // Preload hold: the render is done, the image is decoding — pin the bar
    // at 100% and drop queue/retry/remaining-time affordances (the retained
    // job may have already left the queue, which reads as PENDING here).
    const showAsQueued = isQueued && !isPreloading;
    const displayProgress = isPreloading ? 100 : progress;

    // Proportional inset matches AIGlowBorder's frameThickness formula
    // (ai-glow-border.renderer.ts — `min(w * frameFraction, h / 2)` with frameFraction = 0.15).
    // The animated frame scales with card size, so a fixed-px padding would be fine on mobile
    // but let the waves crawl over the text/progress on desktop/XL where 15% of width far
    // exceeds 96px. Sharing the same formula via `min(15cqw, 50cqh)` keeps content clear of
    // the frame at every breakpoint. On small containers (phone-sized cards) we drop to 10cqw
    // so multi-line facts fit without truncation; the aurora's outermost feathered edge is
    // translucent, so brief overlap reads as a soft halo.
    return (
      <div
        className="absolute inset-0 z-10 [container-type:size]"
      >
        <div
          className={cn(
            // Horizontal padding keeps content clear of the animated aurora frame
            // (min(15cqw,50cqh)); vertical padding is a bit smaller so content
            // isn't crammed into a thin centered band. The fact floats in the
            // flexible top area; the stage line stays glued just above the bar
            // (it's a progress caption), both at the bottom.
            'absolute inset-0 bg-black/60 backdrop-blur-md flex flex-col transition-opacity duration-500 px-[min(10cqw,50cqh)] py-[min(7cqw,22cqh)] @md:px-[min(15cqw,50cqh)]',
            isFadingOut ? 'opacity-0' : 'opacity-100',
            className
          )}
        >
          {/* Fact/tip text — floats in the flexible upper area. No animated icon
              above it; the reserved icon space squeezed the fact text into
              truncation, so it was dropped. Apple/Android mirror. */}
          <div className="flex-1 flex items-center justify-center min-h-0 overflow-hidden w-full">
            {showContent && currentContent && (
              <p
                key={currentContent.text}
                ref={factRef}
                className="text-progress-fact text-center text-white text-balance max-w-md animate-fade-in"
              >
                {currentContent.text}
              </p>
            )}
          </div>

          {/* Progress Bar — bottom. Sits inside the padded area so it also clears
              the animated frame. */}
          <div className="w-full">
            {isRetrying && !isPreloading ? (
              <div className="text-center">
                <p className="text-list-item-value text-white animate-pulse">
                  {tProgress('rethinking')}
                </p>
              </div>
            ) : showAsQueued ? (
              <div className="w-full bg-muted/80 rounded-full h-2 overflow-hidden flex items-center justify-center">
                <LoadingAnimation type="dots" size="sm" />
              </div>
            ) : (
              <div className="space-y-2">
                {/* Design stage — glued directly above the bar, left-aligned. */}
                {currentStage && !isPreloading && (
                  <p
                    key={currentStage}
                    className="text-progress-meta text-start text-white/70 animate-fade-in truncate"
                  >
                    {currentStage}
                  </p>
                )}
                <Progress
                  value={displayProgress}
                  className="h-2 bg-white/20 transition-all duration-300"
                  isStalling={isStalling && !isPreloading}
                />
                <div className="flex items-center justify-between">
                  <span className="text-progress-value text-white">{displayProgress}%</span>
                  {isPreloading ? null : isUpscaleOperation ? (
                    <span className="text-progress-meta text-white/80">
                      {tProgress('upscalingTo4K')}
                    </span>
                  ) : isStalling ? (
                    <span className="text-progress-meta text-white/80 text-balance">
                      {tProgress('takingLonger')}
                    </span>
                  ) : remainingTime ? (
                    <span className="text-progress-meta text-white/80">
                      {formatRemaining(remainingTime)}
                    </span>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Full mode (default)
  if (isFailed) {
    return (
      <Alert variant="destructive" className={className}>
        <AlertDescription className="text-center">
          <div className="font-medium mb-2">
            {entityType === 'render'
              ? t('failed.render')
              : t('failed.control')}
          </div>
          <div className="text-form-help">
            {error || t('failed.unexpected')}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  if (isCompleted) {
    if (wasAlreadyCompleted) {
      return (
        <NoticeBanner
          variant="warning"
          title={t('alreadyExists.title')}
          message={t('alreadyExists.returning')}
          action={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onComplete?.()}
            >
              {t('alreadyExists.returnNow')}
            </Button>
          }
          className={className}
        />
      );
    }

    const completedTitle = isUpscaleOperation
      ? t('completed.upscale')
      : entityType === 'render'
        ? t('completed.render')
        : t('completed.control');
    const completedMessage = isUpscaleOperation
      ? t('completed.upscaleDescription')
      : userStats.renderCount > 0 && entityType === 'render'
        ? t('completed.designsCount', { count: userStats.renderCount })
        : undefined;

    return (
      <NoticeBanner
        variant="success"
        title={completedTitle}
        message={completedMessage}
        className={className}
      />
    );
  }

  return (
    <Card
      className={cn(
        'bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20',
        className
      )}
    >
      <CardContent className="p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          {/* Animation */}
          {showAnimation && (
            <div className="flex justify-center mb-4 md:mb-6 scale-50 md:scale-100">
              <LoadingAnimation
                type={isQueued ? 'blueprint' : animationType}
                size="lg"
              />
            </div>
          )}

          {/* Content Display */}
          {showContent && !isUpscaleOperation && currentContent && (
            <div className="mb-8 text-center animate-fade-in">
              <div className="p-6">
                <p className="text-card-title">{currentContent.text}</p>
              </div>
            </div>
          )}

          {/* Upscale specific content */}
          {isUpscaleOperation && (
            <div className="mb-8 text-center animate-fade-in">
              <div className="p-6">
                <div className="flex items-center justify-center mb-4">
                  <div
                    className="text-section-eyebrow text-white border border-white/50 rounded-full px-3 py-1"
                    style={{ borderWidth: '0.5px' }}
                  >
                    {t('upscale.badge')}
                  </div>
                </div>
                <p className="text-card-title mb-2">
                  {t('upscale.heading')}
                </p>
                <p className="text-section-subtitle text-muted-foreground">
                  {t('upscale.subheading')}
                </p>
              </div>
            </div>
          )}

          {/* Progress Information */}
          <div className="space-y-4">
            {/* Progress Bar or Queue Spinner */}
            {isQueued ? (
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-list-item-value">
                    {t(
                      isUpscaleOperation
                        ? 'queuedLabel.upscale'
                        : entityType === 'render'
                          ? 'queuedLabel.render'
                          : 'queuedLabel.control',
                      { id: entity?.id ?? '' }
                    )}
                  </span>
                  <Badge
                    variant="outline"
                    className="border-yellow-500 text-yellow-600"
                  >
                    {t('waiting')}
                  </Badge>
                </div>
                <div className="w-full bg-muted rounded-full h-3 overflow-hidden flex items-center justify-center">
                  <LoadingAnimation type="dots" size="sm" />
                </div>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-list-item-value">
                    {t(
                      isUpscaleOperation
                        ? 'processingLabel.upscale'
                        : entityType === 'render'
                          ? 'processingLabel.render'
                          : 'processingLabel.control',
                      { id: entity?.id ?? '' }
                    )}
                  </span>
                  <span className="text-list-item-value text-primary">
                    {progress}%
                  </span>
                </div>
                <Progress
                  value={progress}
                  className="h-3"
                  isStalling={isStalling}
                />
              </div>
            )}

            {/* Status Message */}
            <div className="text-center">
              <div className="space-y-2">
                {(isQueued || isUpscaleOperation) && (
                  <p className="text-list-item-value">
                    {isQueued ? t('status.waiting') : t('status.upscaling')}
                  </p>
                )}
                <p className="text-card-meta text-muted-foreground">
                  {isQueued &&
                    (isUpscaleOperation
                      ? t('status.upscaleQueued')
                      : t('status.renderQueued'))}
                  {isProcessing &&
                    isUpscaleOperation &&
                    progress < 50 &&
                    t('status.upscaleEarly')}
                  {isProcessing &&
                    isUpscaleOperation &&
                    progress >= 50 &&
                    t('status.upscaleLate')}
                  {isProcessing &&
                    !isUpscaleOperation &&
                    progress < 50 &&
                    t('status.renderEarly')}
                  {isProcessing &&
                    !isUpscaleOperation &&
                    progress >= 50 &&
                    t('status.renderLate')}
                </p>
              </div>
            </div>

            {/* Remaining Time / Stalling */}
            {isProcessing && (remainingTime || isStalling) && (
              <div className="text-center text-progress-meta text-muted-foreground">
                <p>
                  {isStalling
                    ? tProgress('takingLonger')
                    : formatRemaining(remainingTime)}
                </p>
              </div>
            )}

            {/* Back link */}
            {entityType === 'render' && projectId && conceptId && (
              <div className="mt-6 text-center">
                <p className="text-section-subtitle text-muted-foreground">
                  {t('backHint')}
                </p>
                <Link
                  href={`/project/${projectId}/concept/${conceptId}`}
                  className="inline-block mt-4 text-primary hover:text-primary/80"
                >
                  {t('backToConcept')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
